import PageHeader from "@/components/PageHeader";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/primitives";

const DETAILS = [
  {
    num: "01",
    title: "Design Solutions",
    intro:
      "Design at ARA Turnkey starts with research, how a company works, moves and collaborates, and ends in spaces that are functional, sustainable and unmistakably theirs. Civil, interiors and MEPF are designed as one, never in isolation.",
    bullets: [
      { t: "Civil & Interior & MEPF Design", d: "Structure, interiors and building services developed together by one integrated team." },
      { t: "Project Research", d: "Studies of your people, workflows and culture that shape every design decision." },
      { t: "Optimized Space Planning", d: "Layouts that extract the highest value from every square foot you pay for." },
      { t: "Functional & Sustainable Designs", d: "Spaces engineered for performance, efficiency and long-term flexibility." },
    ],
  },
  {
    num: "02",
    title: "Build Solutions",
    intro:
      "From single floors to full campuses, we build what we design, with our own project teams, procurement muscle and quality systems, not a chain of vendors passing blame.",
    bullets: [
      { t: "Turnkey Interior Solutions", d: "Complete interiors delivered end to end; one contract, one point of accountability." },
      { t: "Interior Fit-out Solutions", d: "Fast, precise fit-outs for new premises and live environments alike." },
      { t: "General Contracting", d: "Full contracting responsibility for scope, safety, schedule and cost." },
    ],
  },
  {
    num: "03",
    title: "Design & Build",
    intro:
      "The fastest route from ambition to move-in, with architecture, interiors, engineering, management and execution under one roof, which is exactly why this company was founded.",
    bullets: [
      { t: "Architecture", d: "Concept to compliance, led by our principal architect." },
      { t: "Interior Design", d: "Experience-led interiors that make every square foot feel intentional." },
      { t: "MEP", d: "Mechanical, electrical and plumbing engineered in parallel, never retrofitted." },
      { t: "Project Management", d: "One team owning scope, vendors, timeline and reporting." },
      { t: "Execution", d: "Site teams that deliver with stage-wise quality checks." },
    ],
  },
  {
    num: "04",
    title: "Engineering & Operations",
    intro:
      "The discipline behind the finish, engineers who keep buildings performing long after the ribbon is cut.",
    bullets: [
      { t: "End-to-End Service Execution", d: "Every service installed, tested and commissioned by our own specialists." },
      { t: "Quality Assurance & Control", d: "Documented checks at every stage, from procurement to snagging." },
      { t: "Integration of Advanced Technologies", d: "Smart building, networking, security and AV systems built in from day one." },
      { t: "Customization of Engineering Solutions", d: "Engineering tailored to your building's realities, not a standard template." },
    ],
  },
];

const SECTORS = [
  { name: "Commercial", desc: "Offices, headquarters and experience centres", img: "https://images.unsplash.com/photo-1758448656987-cfae6bf225e4?q=80&w=1200&auto=format&fit=crop" },
  { name: "Infrastructure", desc: "Transit hubs and public-grade projects", img: "https://images.unsplash.com/photo-1771911650735-b471e85e8b17?q=80&w=1200&auto=format&fit=crop" },
  { name: "Hospitality", desc: "Hotels, banquets and guest experiences", img: "https://images.pexels.com/photos/15227142/pexels-photo-15227142.jpeg?auto=compress&cs=tinysrgb&w=1200" },
  { name: "Data Centres", desc: "Mission-critical, high-density environments", img: "https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg?auto=compress&cs=tinysrgb&w=1200" },
  { name: "Residential", desc: "Premium towers and clubhouse interiors", img: "https://images.unsplash.com/photo-1758193783649-13371d7fb8dd?q=80&w=1200&auto=format&fit=crop" },
  { name: "Institutional", desc: "Campuses and auditoriums", img: "https://images.pexels.com/photos/32731345/pexels-photo-32731345.jpeg?auto=compress&cs=tinysrgb&w=1200" },
];

export default function ServicesPage() {
  return (
    <main className="bg-paper pt-16" data-testid="services-page">
      <PageHeader
        index="02"
        eyebrow="What we do"
        title="Our Services"
        sub="A complete range of services that takes your space from first idea to final handover."
      />

      <section className="px-6 py-20 lg:px-12 lg:py-28">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-24">
          {DETAILS.map((s) => (
            <div key={s.num} className="grid gap-10 lg:grid-cols-12" data-testid={`service-detail-${s.num}`}>
              <div className="lg:col-span-4">
                <div className="lg:sticky lg:top-28">
                  <Reveal>
                    <span className="font-sans text-5xl font-bold text-ink/10">{s.num}</span>
                    <h2 className="mt-4 font-serif text-3xl leading-tight text-ink lg:text-4xl">{s.title}</h2>
                    <p className="mt-6 max-w-xs text-sm leading-relaxed text-ink/65">{s.intro}</p>
                  </Reveal>
                </div>
              </div>
              <div className="lg:col-span-8">
                {s.bullets.map((b, i) => (
                  <Reveal key={b.t} delay={i * 0.06}>
                    <div className="flex flex-col gap-2 border-t border-ink/15 py-7 sm:flex-row sm:gap-10" data-testid={`service-detail-${s.num}-bullet-${i + 1}`}>
                      <h3 className="font-serif text-xl text-navy sm:w-2/5 sm:shrink-0 lg:text-2xl">{b.t}</h3>
                      <p className="text-sm leading-relaxed text-ink/65">{b.d}</p>
                    </div>
                  </Reveal>
                ))}
                <div className="border-t border-ink/15" />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-ink/15 bg-paper px-6 py-20 lg:px-12 lg:py-28" data-testid="sectors-section">
        <div className="mx-auto max-w-[1440px]">
          <Reveal>
            <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-ink/50">
              <span className="text-maroon">+</span> Sectors We've Worked In
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl text-ink sm:text-5xl">
              Every sector and <em className="italic text-navy">every space</em>
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SECTORS.map((sec, i) => (
              <Reveal key={sec.name} delay={(i % 3) * 0.07}>
                <div
                  className="group relative h-64 cursor-default overflow-hidden rounded-2xl"
                  data-testid={`sector-card-${i + 1}`}
                >
                  <img
                    src={sec.img}
                    alt={sec.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <h3 className="font-serif text-2xl text-paper">{sec.name}</h3>
                    <p className="mt-1.5 text-xs leading-relaxed text-paper/75">{sec.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-paper px-6 pb-24 lg:px-12 lg:pb-36" data-testid="services-cta">
        <div className="mx-auto flex max-w-[1440px] flex-col items-start justify-between gap-8 border-t border-ink/15 pt-14 lg:flex-row lg:items-center">
          <Reveal>
            <h2 className="font-serif text-3xl text-ink sm:text-4xl">
              Not sure which service you need?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/contact"
              data-testid="services-contact-button"
              className="inline-block rounded-full bg-maroon px-10 py-4 text-[12px] font-medium uppercase tracking-[0.2em] text-paper transition-transform duration-300 hover:scale-[1.04]"
            >
              Talk to Us
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
